/**
 * UI flag slice — transient boolean flags for modals, drawers, visibility.
 *
 * Ported from legacy `useStore.ts`:
 *   showAddItemModal, showTemplates, showHistory, showStoreManagement,
 *   showHouseholdProductsSettings, showRetailerOffers
 *
 * All six were independent booleans with matching setters. They are grouped
 * here so reducers are uniform and so an app that wants a new flag can add
 * it without extending a bespoke reducer. The flag *names* are still typed
 * as a union — this catches typos (`setUiFlag(s, {flag:'showTemplate', ...})`
 * vs the real `'showTemplates'`).
 */

export type UiFlagName =
  | 'showAddItemModal'
  | 'showTemplates'
  | 'showHistory'
  | 'showStoreManagement'
  | 'showHouseholdProductsSettings'
  | 'showRetailerOffers';

export type UiState = {
  readonly [K in UiFlagName]: boolean;
};

export const initialUiState: UiState = {
  showAddItemModal: false,
  showTemplates: false,
  showHistory: false,
  showStoreManagement: false,
  showHouseholdProductsSettings: false,
  showRetailerOffers: false,
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export function setUiFlag(
  state: UiState,
  input: { flag: UiFlagName; value: boolean }
): UiState {
  // Skip the object rebuild if nothing changed — stable references help
  // subscribers skip renders.
  if (state[input.flag] === input.value) return state;
  return { ...state, [input.flag]: input.value };
}

export function toggleUiFlag(
  state: UiState,
  input: { flag: UiFlagName }
): UiState {
  return { ...state, [input.flag]: !state[input.flag] };
}
